import React, { useEffect, useRef, useState } from "react";
import { Select, Spin } from "antd";
import { LoadingOutlined, SearchOutlined } from "@ant-design/icons";
import useProductOpenApiClient from "apiClients/useProductOpenApiClient";
import { components } from "apiClients/base/schema";

const { Option } = Select;

export type ProductSearchResult = components["schemas"]["ProductResponse"];

type ProductSearchSelectProps = {
  onSelect: (product: ProductSearchResult) => void;
  placeholder?: string;
  style?: React.CSSProperties;
  size?: "small" | "middle" | "large";
  /** Reset internal search state when this key changes (e.g. dialog open). */
  resetKey?: string | number | boolean;
};

export const ProductSearchSelect: React.FC<ProductSearchSelectProps> = ({
  onSelect,
  placeholder = "Sök efter produkt...",
  style,
  size = "middle",
  resetKey,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [results, setResults] = useState<ProductSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string | undefined>();
  const { searchProduct } = useProductOpenApiClient();
  const selectRef = useRef<any>(null);

  useEffect(() => {
    setSearchValue("");
    setResults([]);
    setSelectedValue(undefined);
    setLoading(false);
  }, [resetKey]);

  useEffect(() => {
    if (searchValue && searchValue.length >= 2) {
      setLoading(true);
      const timer = setTimeout(async () => {
        try {
          const searchResults = await searchProduct(searchValue);
          setResults(searchResults || []);
        } catch (error) {
          console.error("Search error:", error);
          setResults([]);
        } finally {
          setLoading(false);
        }
      }, 300);

      return () => clearTimeout(timer);
    }

    setResults([]);
    setLoading(false);
  }, [searchValue, searchProduct]);

  const getImageUrl = (product: ProductSearchResult): string =>
    product.images?.[0]?.previewUrl || "";

  return (
    <Select
      ref={selectRef}
      showSearch
      value={selectedValue}
      placeholder={placeholder}
      style={{ width: "100%", ...style }}
      size={size}
      suffixIcon={loading ? <LoadingOutlined spin /> : <SearchOutlined />}
      filterOption={false}
      onSearch={setSearchValue}
      onSelect={(value: string) => {
        const selected = results.find((item) => item.id?.toString() === value);
        if (selected?.id) {
          onSelect(selected);
          setSelectedValue(undefined);
          setSearchValue("");
          setResults([]);
        }
      }}
      onClear={() => {
        setSelectedValue(undefined);
        setSearchValue("");
        setResults([]);
        setLoading(false);
      }}
      allowClear
      loading={loading}
      notFoundContent={
        loading ? (
          <div style={{ textAlign: "center", padding: 12 }}>
            <Spin size="small" />
            <span style={{ marginLeft: 8 }}>Söker produkter...</span>
          </div>
        ) : searchValue && searchValue.length >= 2 && results.length === 0 ? (
          <div style={{ textAlign: "center", padding: 12, color: "#999" }}>
            Inga produkter hittades
          </div>
        ) : searchValue && searchValue.length < 2 ? (
          <div style={{ textAlign: "center", padding: 12, color: "#999" }}>
            Skriv minst 2 tecken för att söka
          </div>
        ) : null
      }
      dropdownStyle={{ maxHeight: 300, overflow: "auto" }}
      getPopupContainer={() => document.body}
      popupMatchSelectWidth={false}
      dropdownMatchSelectWidth={360}
    >
      {results.map((item) => (
        <Option key={item.id} value={item.id?.toString()}>
          <div style={{ display: "flex", alignItems: "center", padding: "2px 0" }}>
            {getImageUrl(item) && (
              <img
                src={getImageUrl(item)}
                alt={item.name || ""}
                style={{
                  width: 30,
                  height: 30,
                  objectFit: "cover",
                  borderRadius: 3,
                  marginRight: 8,
                  flexShrink: 0,
                }}
              />
            )}
            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
              <div style={{ fontWeight: "bold", fontSize: 13, lineHeight: 1.2 }}>
                {item.name || "Namn saknas"}
              </div>
              <div style={{ fontSize: 11, color: "#666", lineHeight: 1.1 }}>
                {item.mainItemNo && `SKU: ${item.mainItemNo}`}
                {item.brandName && ` • ${item.brandName}`}
              </div>
            </div>
          </div>
        </Option>
      ))}
    </Select>
  );
};
